import classNames from 'classnames'
import { ChangeEvent, useEffect, useState } from 'react'
import { FormInput, FormInputProps, IconFa } from '../../atoms'

export interface FormTimeProps extends Omit<FormInputProps, 'ref' | 'value'> {
  value?: string
  onValueChange: (value: string) => void
}

const completeTimeRegex = /^(0[0-9]|1[0-9]|2[0-3]):?[0-5][0-9]$/
const incompleteTimeRegex = /^\d{0,2}:?\d{0,2}$/

export function FormTime({ onValueChange, ...props }: FormTimeProps) {
  const [localValue, setLocalValue] = useState(props.value ?? '')

  useEffect(() => {
    setLocalValue(props.value ?? '')
  }, [props.value])

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value

    if (!incompleteTimeRegex.test(value)) {
      return
    }

    if (completeTimeRegex.test(value)) {
      if (value.length === 4) {
        value = value.slice(0, 2) + ':' + value.slice(2, 4)
      }
      event.target.value = value
    }

    props.onChange?.(event)
    setLocalValue(value)
  }

  const validateFormatOnBlur = (event: React.FocusEvent<HTMLInputElement, Element>) => {
    props.onBlur?.(event)
    const validTime = getValidTime(localValue)
    onValueChange(validTime)
    setLocalValue(validTime)
  }

  return (
    <FormInput
      {...props}
      value={localValue}
      onBlur={validateFormatOnBlur}
      aria-invalid={props['aria-invalid']}
      className={classNames('min-w-[93px]', props.className)}
      errorMessage={props.errorMessage}
      placeholder={props.placeholder ?? '--:--'}
      onChange={onChange}
      icon={props.icon === undefined ? <IconFa icon={['far', 'clock']} /> : props.icon}
    />
  )
}

const validPartialTimeRegex = /^(0[0-9]|1[0-9]|2[0-3]):?[0-5]?([0-5][0-9])?$/
export const getValidTime = (time: string): string => {
  if (completeTimeRegex.test(time)) {
    return time
  }
  const onlyNumbers = time.replace(':', '')
  if (onlyNumbers === '') {
    return ''
  }
  if (onlyNumbers.length === 1) {
    return `0${time}:00`
  }
  if (validPartialTimeRegex.test(time)) {
    const validTime = (onlyNumbers + '000').slice(0, 4)
    return validTime.slice(0, 2) + ':' + validTime.slice(2, 4)
  } else {
    return getValidTime(onlyNumbers.substring(0, onlyNumbers.length - 1))
  }
}
